import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AdminOrdersAPI } from '../../Services/ECommerceAPI';
import { getShippingAddressLines, getAccountCustomer } from '../../Utils/orderFormat';
import { toast } from 'react-toastify';
import './OrderDetails.css';

const OrderDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [orderStatuses, setOrderStatuses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [selectedStatusId, setSelectedStatusId] = useState('');
    const [trackingNumber, setTrackingNumber] = useState('');
    const [carrier, setCarrier] = useState('');

    useEffect(() => {
        loadOrder();
        loadStatuses();
    }, [id]);

    useEffect(() => {
        if (order) {
            setSelectedStatusId(order.orderStatusID || '');
            setTrackingNumber(order.trackingNumber || '');
            setCarrier(order.carrier || '');
        }
    }, [order]);

    const loadOrder = async () => {
        try {
            setLoading(true);
            const data = await AdminOrdersAPI.getOrderById(parseInt(id));
            setOrder(data);
        } catch (error) {
            console.error('Error loading order:', error);
            toast.error('Failed to load order details');
            navigate('/Home/Admin-Orders');
        } finally {
            setLoading(false);
        }
    };

    const loadStatuses = async () => {
        try {
            const statuses = await AdminOrdersAPI.getOrderStatuses();
            setOrderStatuses(Array.isArray(statuses) ? statuses : []);
        } catch (error) {
            console.error('Error loading statuses:', error);
        }
    };

    const handleStatusUpdate = async (e) => {
        e.preventDefault();
        if (!selectedStatusId || updating) return;
        try {
            setUpdating(true);
            await AdminOrdersAPI.updateOrderStatus(parseInt(id), parseInt(selectedStatusId), trackingNumber || undefined);
            toast.success('Order status updated');
            await loadOrder();
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error(error.response?.data?.message || 'Failed to update status');
        } finally {
            setUpdating(false);
        }
    };

    const handleTrackingUpdate = async (e) => {
        e.preventDefault();
        if (updating) return;
        try {
            setUpdating(true);
            await AdminOrdersAPI.updateTracking(parseInt(id), trackingNumber || '', carrier || '');
            toast.success('Tracking updated');
            await loadOrder();
        } catch (error) {
            console.error('Error updating tracking:', error);
            toast.error(error.response?.data?.message || 'Failed to update tracking');
        } finally {
            setUpdating(false);
        }
    };

    if (loading) {
        return <div className="order-details-loading">Loading order...</div>;
    }

    if (!order) {
        return null;
    }

    const account = getAccountCustomer(order);
    const customerName = account
        ? `${account.firstName} ${account.lastName}`.trim() || account.email
        : 'Guest';
    const customerEmail = account?.email || '—';
    const customerPhone = account?.phone?.trim() || '';
    const shippingLines = getShippingAddressLines(order);

    return (
        <div className="order-details-page">
            <div className="order-details-header">
                <button type="button" className="back-btn" onClick={() => navigate('/Home/Admin-Orders')}>
                    ← Back to Orders
                </button>
                <h1>Order #{order.orderNumber}</h1>
                <span className={`status-badge status-${(order.orderStatus?.statusName || '').toLowerCase().replace(/\s/g, '-')}`}>
                    {order.orderStatus?.statusName || 'Unknown'}
                </span>
            </div>

            <div className="order-details-grid">
                <div className="order-details-main">
                    <section className="order-details-card">
                        <h2>Order Info</h2>
                        <div className="info-grid">
                            <div className="info-row">
                                <span className="label">Order Number</span>
                                <span className="value">{order.orderNumber}</span>
                            </div>
                            <div className="info-row">
                                <span className="label">Order Date</span>
                                <span className="value">{new Date(order.orderDate).toLocaleString()}</span>
                            </div>
                            <div className="info-row">
                                <span className="label">Payment</span>
                                <span className="value">{order.paymentMethod || '—'}</span>
                            </div>
                            <div className="info-row">
                                <span className="label">Shipping Method</span>
                                <span className="value">{order.shippingMethod || '—'}</span>
                            </div>
                        </div>
                    </section>

                    <section className="order-details-card contact-card">
                        <h2>Customer contact</h2>
                        <div className="info-grid">
                            <div className="info-row">
                                <span className="label">Account name</span>
                                <span className="value">{customerName}</span>
                            </div>
                            <div className="info-row">
                                <span className="label">Email</span>
                                <span className="value">{account ? customerEmail : '— (guest checkout)'}</span>
                            </div>
                            <div className="info-row">
                                <span className="label">Account phone</span>
                                <span className="value">{customerPhone || '—'}</span>
                            </div>
                            <div className="info-row">
                                <span className="label">Shipping phone</span>
                                <span className="value">{order.shippingPhone || order.ShippingPhone || '—'}</span>
                            </div>
                        </div>
                    </section>

                    <section className="order-details-card">
                        <h2>Items</h2>
                        <table className="order-items-table">
                            <thead>
                                <tr>
                                    <th>Product</th>
                                    <th>SKU</th>
                                    <th>Qty</th>
                                    <th>Unit Price</th>
                                    <th>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(order.orderItems || []).map((item) => (
                                    <tr key={item.orderItemID}>
                                        <td>{item.productName}</td>
                                        <td>{item.productSKU || '—'}</td>
                                        <td>{item.quantity}</td>
                                        <td>${Number(item.unitPrice).toFixed(2)}</td>
                                        <td>${Number(item.totalPrice).toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>

                    <section className="order-details-card shipping-card">
                        <h2>Shipping address</h2>
                        <div className="shipping-address-block">
                            {shippingLines.map((line) => (
                                <div key={line.label} className="shipping-address-line">
                                    <span className="shipping-line-label">{line.label}</span>
                                    <span className="shipping-line-value">{line.value}</span>
                                </div>
                            ))}
                        </div>
                    </section>
                </div>

                <div className="order-details-sidebar">
                    <section className="order-details-card totals-card">
                        <h2>Totals</h2>
                        <div className="totals-rows">
                            <div className="totals-row">
                                <span>Subtotal</span>
                                <span>${Number(order.subTotal).toFixed(2)}</span>
                            </div>
                            <div className="totals-row">
                                <span>Tax</span>
                                <span>${Number(order.taxAmount).toFixed(2)}</span>
                            </div>
                            <div className="totals-row">
                                <span>Shipping</span>
                                <span>${Number(order.shippingAmount).toFixed(2)}</span>
                            </div>
                            {Number(order.discountAmount) > 0 && (
                                <div className="totals-row">
                                    <span>Discount</span>
                                    <span>-${Number(order.discountAmount).toFixed(2)}</span>
                                </div>
                            )}
                            <div className="totals-row total">
                                <span>Total</span>
                                <span>${Number(order.totalAmount).toFixed(2)}</span>
                            </div>
                        </div>
                    </section>

                    <section className="order-details-card">
                        <h2>Update Status</h2>
                        <form onSubmit={handleStatusUpdate} className="status-form">
                            <div className="form-group">
                                <label>Status</label>
                                <select
                                    value={selectedStatusId}
                                    onChange={(e) => setSelectedStatusId(e.target.value)}
                                    disabled={updating}
                                >
                                    {orderStatuses.map((s) => (
                                        <option key={s.statusID} value={s.statusID}>{s.statusName}</option>
                                    ))}
                                </select>
                            </div>
                            <button type="submit" className="submit-btn" disabled={updating}>
                                {updating ? 'Updating...' : 'Update Status'}
                            </button>
                        </form>
                    </section>

                    <section className="order-details-card">
                        <h2>Tracking</h2>
                        <form onSubmit={handleTrackingUpdate} className="tracking-form">
                            <div className="form-group">
                                <label>Tracking Number</label>
                                <input
                                    type="text"
                                    value={trackingNumber}
                                    onChange={(e) => setTrackingNumber(e.target.value)}
                                    placeholder="Optional"
                                    disabled={updating}
                                />
                            </div>
                            <div className="form-group">
                                <label>Carrier</label>
                                <input
                                    type="text"
                                    value={carrier}
                                    onChange={(e) => setCarrier(e.target.value)}
                                    placeholder="e.g. UPS, FedEx"
                                    disabled={updating}
                                />
                            </div>
                            <button type="submit" className="submit-btn" disabled={updating}>
                                {updating ? 'Updating...' : 'Update Tracking'}
                            </button>
                        </form>
                    </section>

                    {order.customerNotes && (
                        <section className="order-details-card">
                            <h2>Customer Notes</h2>
                            <p className="customer-notes">{order.customerNotes}</p>
                        </section>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OrderDetails;
